import React, { useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { isNumber, round } from 'lodash';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import { SwellArrowIcon } from '@wavetrak/theme';

import { BRAZE_CONTENT_CARDS, PRODUCT_TUTORIALS } from 'common/constants';
import {
  SL_WEB_FORECAST_FEEDBACK_FREQUENCY,
  SL_WEB_FORECAST_GRAPHS_MODERNIZATION,
  SL_WEB_RATE_SURF,
} from 'common/treatments';
import ConditionObservation from 'components/ConditionObservation';
import ConditionObservationObservedBy from 'components/ConditionObservationObservedBy';
import ContentCard from 'components/ContentCard';
import CurrentDateTime from 'components/CurrentDateTime';
import CurrentTideConditions from 'components/CurrentTideConditions';
import CurrentWaveRating from 'components/CurrentWaveRating';
import CurrentWaterTemperatures from 'components/CurrentWaterTemperatures';
import CurrentWindMap from 'components/CurrentWindMap';
import Expand from 'components/Icons/Expand';
import ForecastFeedbackContainer from 'components/ForecastFeedbackContainer/ForecastFeedbackContainer';
import useForecastFeedbackUserSettings from 'components/ForecastFeedbackContainer/hooks/useForecastFeedbackUserSettings';
import getPromptFrequency from 'components/ForecastFeedbackContainer/utils/getPromptFrequency';
import Information from 'components/Information';
import LiveWindModal from 'components/LiveWindModal/LiveWindModal';
import LiveWindPaywall from 'components/LiveWindPaywall';
import JSBoardBuilderCTA from 'components/JSBoardBuilderCTA';
import SubregionForecastContentGrouped from 'components/SubregionForecastContentGrouped';
import DaysToWatchCTA from 'components/DaysToWatchCTA';
import Modal from 'components/Modal/Modal';
import { getSpotReportAssociated } from 'selectors/spot';
import { getForecastContent } from 'selectors/subregion';
import { useUserDetails, useUserPermissionStatus } from 'selectors/user';
import { useAppSelector } from 'stores/hooks';
import type {
  SpotDetails,
  SpotForecast,
  SpotReportAssociated,
  SpotReport as TSpotReport,
  SpotReportNotes,
  SwellPoint,
} from 'types/spot';
import { ConditionObservers } from 'types/observations';
import type { SegmentProperties } from 'types/analytics';
import type { ForecastContent } from 'types/subregion';
import mapDegreesCardinal from 'utils/mapDegreesCardinal';
import conditionIsFlat from 'utils/conditionIsFlat';
import { useTreatments } from 'utils/treatments';

import styles from './SpotCurrentConditions.module.scss';
import { getAccessToken } from 'common/baseFetch';

const conditionRatingClipPathIds = {
  barGraphIcon: 'condition-rating-bar-graph-icon',
};

const surfHeightClipPathIds = {
  barGraphIcon: 'surf-height-bar-graph-icon',
};

const swellClipPathIds = {
  barGraphIcon: 'swell-bar-graph-icon',
};

const windClipPathIds = {
  barGraphIcon: 'wind-bar-graph-icon',
};

const contentCardConfig = {
  name: BRAZE_CONTENT_CARDS.SPOT_CURRENT_CONDITIONS,
  extras: {
    backgroundColor: '',
    body: '',
    closeButtonColor: '',
    fontColor: '',
    linkText: '',
    linkUrl: '',
  },
};

export interface SpotCurrentConditionsProps {
  report: TSpotReport['report'];
  reportAssociated: SpotReportAssociated;
  reportData: SpotForecast;
  reportNotes: SpotReportNotes;
  spot: SpotDetails;
  onOpenRateTheSurf?: () => void;
}
const EMPTY_REPORT = '<p><br></p>';

const SwellConditionItem = ({
  swell,
  index,
  reportAssociated,
}: {
  swell: SwellPoint;
  index: number;
  reportAssociated: SpotReportAssociated;
}) => {
  // do not render swell if it is not a number or less than or equal to 0
  if (!isNumber(swell?.height) || round(swell.height, 1) <= 0) return null;

  const swellDisplayHeight = swell?.height >= 10 ? round(swell.height, 0) : round(swell.height, 1);

  return (
    <Stack
      alignItems="center"
      className={classNames(styles.swellConditions, {
        [styles.primarySwell]: index === 0,
      })}
      data-testid="current-surf-conditions-card__swell"
      direction="row"
      key={`swell-condition-item-${index + 1}`}
      spacing="5px"
    >
      <Typography
        variant={index === 0 ? 'callout2' : 'caption1'}
        textTransform="lowercase"
        display="inline-block"
        component="span"
        className={styles.swellHeight}
      >
        {swellDisplayHeight}
        {reportAssociated.units.swellHeight}
      </Typography>
      <Typography
        variant={index === 0 ? 'callout2' : 'caption1'}
        textTransform="lowercase"
        display="inline-block"
        component="span"
        className={styles.swellPeriod}
      >
        {round(swell.period)}s
      </Typography>
      <div className={styles.swellArrowContainer}>
        <SwellArrowIcon
          size="small"
          rotation={swell.direction - 180}
          className={styles.swellArrow}
        />
      </div>
      <Typography
        variant="caption1"
        display="inline-block"
        component="span"
        className={classNames({
          [styles.swellDirection]: true,
        })}
      >
        {mapDegreesCardinal(swell.direction)}
      </Typography>
      <Typography
        variant="callout2"
        display="inline-block"
        component="span"
        className={styles.swellDirection}
      >
        {round(swell.direction)}º
      </Typography>
    </Stack>
  );
};

const SpotCurrentConditions: React.FC<SpotCurrentConditionsProps> = ({
  report,
  reportAssociated,
  reportData,
  reportNotes,
  spot,
  onOpenRateTheSurf,
}) => {
  const treatments = useTreatments();
  const userDetails = useUserDetails();
  const isAnonymous = !userDetails;

  const { abbrTimezone, utcOffset } = useAppSelector(getSpotReportAssociated);
  const forecastContent: ForecastContent = useAppSelector(getForecastContent);
  const { hasCoreForecastPermissions } = useUserPermissionStatus();
  const showDaysToWatchCTA = !hasCoreForecastPermissions;
  const isRateSurfEnabled = treatments?.[SL_WEB_RATE_SURF] === 'on';
  const isGraphs202603 = treatments?.[SL_WEB_FORECAST_GRAPHS_MODERNIZATION] === 'on';

  const accessToken = useMemo(() => getAccessToken(), []);

  const showReport = !!report && report?.body !== EMPTY_REPORT;

  const { isPromptVisible, isSnoozePromptVisible, updateForecastFeedbackUserSetting } =
    useForecastFeedbackUserSettings({
      isAnonymous,
      promptFrequency: getPromptFrequency(
        treatments[SL_WEB_FORECAST_FEEDBACK_FREQUENCY]?.toString(),
      ),
    });

  // get content for today from the spot report data
  const forecastContentAndReport: ForecastContent | null = useMemo(() => {
    if (!showReport || !forecastContent) return null;

    const todaysFormattedDate = format(
      toZonedTime(new Date().getTime(), reportAssociated.timezone),
      'yyyy-MM-dd',
    );
    return {
      timezone: reportAssociated.timezone,
      lastPublished: report.timestamp,
      location: {
        lon: spot.lon,
        lat: spot.lat,
      },
      subregion: {
        name: spot.subregion.name,
        id: spot.subregion._id,
      },
      forecaster: {
        name: report.forecaster.name,
        avatarUrl: report.forecaster.iconUrl,
      },
      today: {
        date: todaysFormattedDate,
        forecast: {
          dayToWatch: report.dayToWatch,
          headline: report.headline,
          observation: report.body,
        },
      },
      tomorrow: forecastContent.tomorrow,
      tomorrowIsLive: false, // always show today on the left here
      highlights: forecastContent.highlights,
    };
  }, [forecastContent, report, reportAssociated, showReport, spot]);

  const [isLiveWindModalOpen, setIsLiveWindModalOpen] = useState<boolean>(false);
  const [isPersonalizedModalOpen, setIsPersonalizedModalOpen] = useState<boolean>(false);

  // Note: swells are pre-sorted from the Spot Report View Builder
  const sortedTop3Swells = useMemo(() => {
    const swells = reportData?.swells?.slice(0, 3) || [];

    // Check if all swells have the specified "empty" values
    const allSwellsAreEmpty =
      swells.length > 0 &&
      swells.every(
        (swell) =>
          swell.direction === 0 &&
          swell.directionMin === 0 &&
          swell.height === 0 &&
          swell.period === 0,
      );

    return allSwellsAreEmpty ? [] : swells;
  }, [reportData?.swells]);

  const currentConditionsContainerClass = useMemo(
    () =>
      classNames({
        [styles.currentConditionsContainer]: true,
        [styles.showReportInCurrentConditions]: showReport,
      }),
    [showReport],
  );

  const forecastNote = useMemo(() => {
    const { subregion: subregionNote, spot: spotNote } = reportNotes;
    if (subregionNote || spotNote) {
      return `${subregionNote || ''}${subregionNote && spotNote ? '<br /><br />' : ''}${
        spotNote || ''
      }`;
    }
    return undefined;
  }, [reportNotes]);

  const informationSegmentProps = useMemo(
    () => ({
      modalType: 'tutorial',
      spotId: spot._id,
      spotName: spot.name,
      subregionId: spot.subregion._id,
      subregionName: spot.subregion.name,
      tabName: 'report',
      uiElement: 'lotus-information',
    }),
    [spot],
  );

  const observedSurf = useMemo(() => {
    let observedSurfObj = {
      by: 'LOTUS' as ConditionObservers,
      iconUrl: undefined as string | undefined,
      at: null as number | null,
    };

    if (reportData?.waveHeight?.type && reportData?.waveHeight?.type === 'CAMERA') {
      observedSurfObj = { ...observedSurfObj, by: 'Smart Cam' as ConditionObservers };
    }

    if (reportData?.waveHeight?.type && reportData?.waveHeight?.type === 'HUMAN') {
      observedSurfObj = { ...observedSurfObj, by: 'Forecaster' as ConditionObservers };
    }

    if (typeof reportData?.waveHeight?.forecaster === 'object') {
      if (reportData?.waveHeight?.forecaster?.iconUrl) {
        observedSurfObj = {
          ...observedSurfObj,
          iconUrl: reportData?.waveHeight?.forecaster.iconUrl as string,
        };
      }
    }

    if (reportData?.waveHeight?.lastObserved) {
      observedSurfObj = { ...observedSurfObj, at: reportData?.waveHeight?.lastObserved };
    }

    return observedSurfObj;
  }, [reportData?.waveHeight]);

  const observedConditions = useMemo(() => {
    let observedConditionsObj = {
      by: 'LOTUS' as ConditionObservers,
      iconUrl: undefined as string | undefined,
      at: null as number | null,
    };

    if (reportData?.waveHeight?.type && reportData?.waveHeight?.type === 'HUMAN') {
      observedConditionsObj = { ...observedConditionsObj, by: 'Forecaster' as ConditionObservers };
    }

    if (typeof reportData?.waveHeight?.forecaster === 'object') {
      if (reportData?.waveHeight?.forecaster?.iconUrl) {
        observedConditionsObj = {
          ...observedConditionsObj,
          iconUrl: reportData?.waveHeight?.forecaster.iconUrl as string,
        };
      }
    }

    if (reportData?.waveHeight?.lastObserved) {
      observedConditionsObj = {
        ...observedConditionsObj,
        at: reportData?.waveHeight?.lastObserved,
      };
    }

    return observedConditionsObj;
  }, [reportData?.waveHeight]);

  const observedWind = useMemo(() => {
    let observedWindObj = {
      value: '0',
      description: '',
      by: 'GFS' as ConditionObservers,
      at: null as number | null,
    };

    if (reportData?.wind) {
      observedWindObj = { ...observedWindObj, value: reportData?.wind.speed.toString() };
    }

    if (reportData?.wind?.gust && reportData?.wind?.directionType) {
      observedWindObj = {
        ...observedWindObj,
        description: `${reportData?.wind?.gust}${
          reportAssociated?.units.windSpeed?.toLowerCase() || 'mph'
        } gusts, ${reportData?.wind?.directionType}`,
      };
    }

    if (reportData?.wind?.lastObserved) {
      observedWindObj = { ...observedWindObj, by: 'Wind Station' as ConditionObservers };
    }

    if (reportData?.wind?.lastObserved) {
      observedWindObj = { ...observedWindObj, at: reportData?.wind.lastObserved };
    }

    return observedWindObj;
  }, [reportAssociated?.units?.windSpeed, reportData?.wind]);

  const segmentProperties: SegmentProperties = useMemo(
    () => ({
      location: 'Live Wind Full Screen Modal',
      category: 'cams & reports',
      pageName: 'Spot Report',
      spotId: spot._id,
      spotName: spot.name,
    }),
    [spot],
  );

  const geoCoordinates = useMemo(
    () => ({
      lat: spot?.lat,
      lon: spot?.lon,
    }),
    [spot?.lat, spot?.lon],
  );

  const liveWindClickHandler = useCallback(() => {
    setIsLiveWindModalOpen((prev: boolean) => !prev);
  }, []);

  const handleOpenRateSurfClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      if (onOpenRateTheSurf) onOpenRateTheSurf();
    },
    [onOpenRateTheSurf],
  );

  const handleOpenPersonalizedRatings = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      setIsPersonalizedModalOpen(true);
    },
    [],
  );

  const isSurfFlat = useMemo(
    () => conditionIsFlat(reportData?.waveHeight?.max, reportAssociated?.units?.waveHeight),
    [reportData?.waveHeight, reportAssociated?.units?.waveHeight],
  );

  const surfHeightClipPathIdsProps = useMemo(
    () => ({
      clipPathIds: surfHeightClipPathIds,
      description: isSurfFlat ? '' : reportData?.waveHeight?.humanRelation,
      fullHeight: true,
      iconUrl: observedSurf.iconUrl,
      observedAt: observedSurf.at,
      observedBy: observedSurf.by,
      observationValue: isSurfFlat
        ? 'Flat'
        : `${reportData?.waveHeight?.min}-${reportData?.waveHeight?.max}`,
      timezone: reportAssociated?.timezone,
      unit: isSurfFlat ? '' : reportAssociated?.units?.waveHeight,
      isPlus: !!reportData?.waveHeight?.plus && !isSurfFlat,
    }),
    [observedSurf, reportAssociated, reportData, isSurfFlat],
  );

  const swellList = useMemo(() => {
    if (sortedTop3Swells.length === 0)
      return (
        <Typography variant="callout1" className={styles.noData}>
          No data
        </Typography>
      );
    return sortedTop3Swells.map((swell, index) => (
      <SwellConditionItem
        key={`swell-condition-item-${index + 1}`}
        swell={swell}
        index={index}
        reportAssociated={reportAssociated}
      />
    ));
  }, [sortedTop3Swells, reportAssociated]);

  return (
    <>
      <Box className={currentConditionsContainerClass}>
        <>
          {!isGraphs202603 && (
            <>
              <Typography className={styles.currentSurfConditionsHeader} variant="title3">
                Current Surf Conditions
              </Typography>
              <Box className={styles.localTimeObservationClarity}>
                <CurrentDateTime
                  abbrTimezone={reportAssociated?.abbrTimezone}
                  utcOffset={reportAssociated?.utcOffset}
                />
              </Box>
            </>
          )}
          {isRateSurfEnabled && (
            <>
              <Stack direction="row" gap={2} width="100%" mt={2}>
                {onOpenRateTheSurf && spot.cameras?.length > 0 && (
                  <Box>
                    <Button
                      aria-label="Rate the Surf"
                      className={classNames(styles.rateTheSurfButton, {
                        [styles.graphs202603]: isGraphs202603,
                      })}
                      data-testid="rate-the-surf-button"
                      disableFocusRipple
                      disableRipple
                      disableTouchRipple
                      onClick={handleOpenRateSurfClick}
                      size="small"
                      variant="secondary"
                    >
                      Power Your Profile
                    </Button>
                  </Box>
                )}
                <Box>
                  <Button
                    aria-label="View Personalized Ratings"
                    className={classNames(styles.rateTheSurfButton, {
                      [styles.graphs202603]: isGraphs202603,
                    })}
                    data-testid="personalized-ratings-button"
                    disableFocusRipple
                    disableRipple
                    disableTouchRipple
                    onClick={handleOpenPersonalizedRatings}
                    size="small"
                    variant="secondary"
                  >
                    Personalized Ratings
                  </Button>
                </Box>
              </Stack>
              <Modal open={isPersonalizedModalOpen} setOpen={setIsPersonalizedModalOpen}>
                <div
                  className={styles.personalizedRatingsModal}
                  data-testid="personalized-ratings-modal"
                >
                  <iframe
                    title="Personalized Ratings"
                    src={`https://personalization-production.up.railway.app/?token=${accessToken}&spotId=${spot._id}${userDetails?._id ? `&userId=${userDetails._id}` : ''}`}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                  />
                </div>
              </Modal>
            </>
          )}

          <Box
            className={styles.conditionsWrapper}
            data-testid="current-conditions-content-grid-with-clarity"
          >
            <Box className={isPromptVisible ? styles.container : styles.containerNoPrompt}>
              <Box
                className={classNames(styles.box0, styles.box, styles.feedbackRatingsContainer, {
                  [styles.graphs202603]: isGraphs202603,
                })}
              >
                {reportData?.conditions?.value && (
                  <Box
                    className={styles.currentConditionCard}
                    data-testid="spot-current-conditions-condition-rating"
                  >
                    <Stack direction="row" justifyContent="space-between" width="100%" mb={1}>
                      <Typography component="h3" variant="caption2" className={styles.heading}>
                        Condition Rating
                      </Typography>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <ConditionObservationObservedBy
                          clipPathIds={conditionRatingClipPathIds}
                          observedAt={observedConditions.at}
                          observedBy={observedConditions.by}
                          timezone={reportAssociated.timezone}
                        />
                        <Box className={styles.informationWrapper}>
                          <Information
                            articleId={PRODUCT_TUTORIALS.articles.conditionRating.id}
                            className={styles.information}
                            segmentProps={informationSegmentProps}
                            tooltip={PRODUCT_TUTORIALS.articles.conditionRating.tooltip}
                            tutorialId={PRODUCT_TUTORIALS.tutorials.default.id}
                          />
                        </Box>
                      </Stack>
                    </Stack>
                    <CurrentWaveRating rating={reportData?.conditions?.value} size="medium" />
                  </Box>
                )}
              </Box>
              <Box
                id="box1"
                className={classNames(styles.box1, styles.box, styles.surfCard, {
                  [styles.graphs202603]: isGraphs202603,
                })}
                data-testid="current-conditions-content-surf-height-container"
              >
                <Typography
                  component="h3"
                  variant="caption2"
                  className={classNames(styles.heading, styles.surfHeading)}
                  mb={2}
                >
                  Surf Height
                </Typography>
                <ConditionObservation {...surfHeightClipPathIdsProps} />
              </Box>
              <Box
                id="box2"
                className={classNames(styles.box2, styles.box, {
                  [styles.graphs202603]: isGraphs202603,
                })}
                data-testid="current-conditions-content-swell-container"
              >
                <Typography
                  component="h3"
                  variant="caption2"
                  className={classNames(styles.heading, styles.swellHeading)}
                  mb={2}
                >
                  Swell
                </Typography>
                <Box className={styles.cardContent}>
                  <Box className={styles.swells}>{swellList}</Box>
                  <Box>
                    <ConditionObservationObservedBy
                      clipPathIds={swellClipPathIds}
                      observedAt={null}
                      observedBy="LOTUS"
                      timezone={reportAssociated.timezone}
                    />
                  </Box>
                </Box>
              </Box>
              <Box
                id="box3"
                className={classNames(styles.box3, styles.box, {
                  [styles.graphs202603]: isGraphs202603,
                })}
                data-testid="current-conditions-content-wind-container"
              >
                <Box className={styles.windHeader}>
                  <LiveWindModal
                    geo={geoCoordinates}
                    open={isLiveWindModalOpen}
                    setOpen={setIsLiveWindModalOpen}
                    spotId={spot._id}
                    paywall={<LiveWindPaywall segmentProperties={segmentProperties} />}
                  />
                  <Typography
                    component="h3"
                    variant="caption2"
                    className={classNames(styles.heading, styles.windHeading)}
                    mb={2}
                  >
                    Wind
                  </Typography>
                </Box>
                <Box className={styles.windContent}>
                  <Box className={styles.windObservation}>
                    <ConditionObservation
                      clipPathIds={windClipPathIds}
                      description={observedWind.description}
                      fullHeight
                      observedAt={observedWind.at}
                      observedBy={observedWind.by}
                      observationValue={observedWind.value}
                      observationValueSuffix={mapDegreesCardinal(reportData?.wind?.direction)}
                      timezone={reportAssociated.timezone}
                      unit={reportAssociated.units.windSpeed}
                    />
                  </Box>
                  <Box className={styles.windMapContainer}>
                    {spot.hasLiveWind && (
                      <IconButton
                        aria-label={`Live wind report for ${spot.name}`}
                        className={styles.liveWindButton}
                        data-testid="spot-current-conditions-live-wind-button"
                        disableFocusRipple
                        disableRipple
                        disableTouchRipple
                        onClick={liveWindClickHandler}
                      >
                        <Expand />
                      </IconButton>
                    )}
                    <Box className={styles.windMap}>
                      <CurrentWindMap
                        geo={geoCoordinates}
                        isLiveAvailable={spot.hasLiveWind}
                        loopAnimation={spot.hasLiveWind}
                        changeAnimation={false}
                        isTooltipMode={false}
                        size="large"
                        windDirection={reportData?.wind?.direction}
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>
              <Box
                id="box4"
                className={classNames(styles.box4, styles.box, styles.tideCard, {
                  [styles.graphs202603]: isGraphs202603,
                })}
              >
                <Typography component="h3" variant="caption2" className={styles.heading} mb={1}>
                  Tide
                </Typography>
                <CurrentTideConditions units={reportAssociated.units} tide={reportData.tide} />
              </Box>
              <Box
                id="box5"
                className={classNames(styles.box5, styles.box, {
                  [styles.graphs202603]: isGraphs202603,
                })}
                data-testid="current-conditions-content-temperature-container"
              >
                <Typography component="h3" variant="caption2" className={styles.heading} mb={1}>
                  Temperature
                </Typography>
                <CurrentWaterTemperatures
                  units={reportAssociated.units}
                  waterTemp={reportData.waterTemp}
                  weather={reportData.weather}
                  weatherIconPath={reportAssociated.weatherIconPath}
                  wetsuit={reportData.wetsuit}
                />
              </Box>
              <Box
                id="box6"
                className={classNames(styles.box6, styles.box, styles.jsBoardBuilderCTA, {
                  [styles.graphs202603]: isGraphs202603,
                })}
              >
                <JSBoardBuilderCTA />
              </Box>
              {isPromptVisible && (
                <Box
                  id="box7"
                  className={classNames(styles.box7, styles.box, styles.forecastFeedback, {
                    [styles.graphs202603]: isGraphs202603,
                  })}
                >
                  <ForecastFeedbackContainer
                    spotId={spot._id}
                    spotName={spot.name}
                    timezone={abbrTimezone}
                    utcOffset={utcOffset}
                    isPromptVisible={isPromptVisible}
                    isSnoozePromptVisible={isSnoozePromptVisible}
                    updateForecastFeedbackUserSetting={updateForecastFeedbackUserSetting}
                  />
                </Box>
              )}
            </Box>
          </Box>
        </>
      </Box>
      <div className={styles.contentCardWrapper}>
        <ContentCard
          card={contentCardConfig}
          className={styles.contentCard}
          testId="banner-braze-card-current-condtions"
        />
      </div>
      {forecastContentAndReport && (
        <Box className={styles.currentConditionsReportContainer}>
          <SubregionForecastContentGrouped
            className={styles.forecastContentCard}
            forecastContent={forecastContentAndReport}
            forecastNote={forecastNote}
            isLoading={false}
            pageName="Spot Report"
            spotId={spot._id}
          />
          <Box className={styles.daysToWatchCTAContainer}>
            {showDaysToWatchCTA && <DaysToWatchCTA pageName="forecast" />}
          </Box>
        </Box>
      )}
    </>
  );
};

export default SpotCurrentConditions;
